import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Calendar, CheckCircle, Clock } from 'lucide-react';
import { CONTACT_INFO } from '../data/mock';

const BookCall = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-50 to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Book a 20-minute call</h1>
          <p className="text-xl text-gray-600">
            We'll confirm your goals, review your route, and make sure the structure matches 
            what you want to build in Mexico.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left: What we'll cover */}
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-6">What we'll cover on the call</h2>
              <div className="space-y-4">
                <Card className="border-gray-200">
                  <CardContent className="flex items-start space-x-4 pt-6">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Your project goals</h3>
                      <p className="text-gray-600">
                        We'll review what you're building in Mexico and your business objectives.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="flex items-start space-x-4 pt-6">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Recommended route</h3>
                      <p className="text-gray-600">
                        We'll confirm the right service path based on your structure and needs.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="flex items-start space-x-4 pt-6">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Timeline and next steps</h3>
                      <p className="text-gray-600">
                        We'll outline the incorporation process and what documents you'll need.
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-gray-200">
                  <CardContent className="flex items-start space-x-4 pt-6">
                    <div className="flex-shrink-0 w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-blue-900" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-2">Your questions</h3>
                      <p className="text-gray-600">
                        We'll answer any specific questions about your project or the process.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div className="mt-8 bg-blue-50 border border-blue-100 rounded-lg p-6">
                <div className="flex items-start space-x-3">
                  <Clock className="w-5 h-5 text-blue-900 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">No payment before validation</h3>
                    <p className="text-gray-700">
                      Payment comes only after we confirm your route and structure alignment on the call.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6">
                <p className="text-sm text-gray-600 mb-2">
                  Prefer to start with your incorporation request first?
                </p>
                <Link to="/start">
                  <Button variant="outline" className="border-gray-300 hover:border-blue-900 hover:text-blue-900">
                    Start Incorporation
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right: Calendar Placeholder */}
            <div>
              <Card className="border-2 border-gray-200">
                <CardContent className="p-8">
                  <div className="flex flex-col items-center justify-center min-h-[500px] space-y-6">
                    <Calendar className="w-16 h-16 text-blue-900" />
                    <h3 className="text-xl font-semibold text-gray-900 text-center">
                      Schedule Your Call
                    </h3>
                    <p className="text-gray-600 text-center max-w-md">
                      Our booking calendar will be embedded here. For now, please contact us directly 
                      to schedule your 20-minute validation call.
                    </p>
                    <div className="space-y-3 w-full">
                      <a 
                        href={CONTACT_INFO.calendly}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <Button className="w-full bg-blue-900 hover:bg-blue-800 text-white">
                          Open booking calendar
                        </Button>
                      </a>
                      <a 
                        href={`mailto:${CONTACT_INFO.email}`}
                        className="block"
                      >
                        <Button variant="outline" className="w-full border-gray-300 hover:border-blue-900 hover:text-blue-900">
                          Email us instead
                        </Button>
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="mt-6 text-center">
                <p className="text-sm text-gray-600">
                  Questions? Email us at{' '}
                  <a href={`mailto:${CONTACT_INFO.email}`} className="text-blue-900 hover:underline">
                    {CONTACT_INFO.email}
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BookCall;
